import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="py-10 px-6 border-t" style={{
      backgroundColor: 'var(--bg-surface)',
      borderColor: 'var(--border-subtle)'
    }} data-id="element-202">
        <div className="max-w-[680px] mx-auto" data-id="element-203">
          {/* Payment trust badges */}
          <div className="flex items-center justify-center gap-3 mb-6 flex-wrap" data-id="element-204">
            {['Visa', 'Mastercard', 'PayPal', 'Amex', 'Apple Pay'].map(method => <div key={method} className="bg-[var(--bg-card)] border border-[var(--border-subtle)] rounded px-3 py-1.5 text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-wider shadow-sm" data-id="element-205">
                  {method}
                </div>)}
          </div>

          <div className="flex justify-center items-center gap-6" data-id="element-206">
            <button onClick={() => setActiveModal('privacy')} className="text-sm text-[var(--text-muted)] hover:text-[var(--accent)] transition-colors cursor-pointer" data-id="element-207">
              Política de Privacidad
            </button>
            <span className="text-[var(--bg-elevated)]" data-id="element-208">|</span>
            <button onClick={() => setActiveModal('terms')} className="text-sm text-[var(--text-muted)] hover:text-[var(--accent)] transition-colors cursor-pointer" data-id="element-209">
              Términos y Condiciones
            </button>
          </div>
          <p className="text-center text-xs text-[var(--text-muted)] opacity-50 mt-4" data-id="element-210">
            © {new Date().getFullYear()} Apaga La Mente. Todos los derechos
            reservados.
          </p>
          <p className="text-center text-[10px] text-[var(--text-muted)] opacity-30 mt-2" data-id="element-211">
            Este es un producto digital. No se enviará ningún producto físico.
          </p>
        </div>
      </footer>

      <AnimatePresence data-id="element-212">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.25
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setActiveModal(null)} data-id="element-213">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.3
        }} onClick={e => e.stopPropagation()} className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-sm w-full max-w-2xl max-h-[80vh] overflow-hidden shadow-xl" data-id="element-214">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-subtle)]" data-id="element-215">
                <h3 className="text-lg font-serif text-[var(--text-heading)]" data-id="element-216">
                  {activeModal === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones'}
                </h3>
                <button onClick={() => setActiveModal(null)} className="text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors p-1" aria-label="Cerrar" data-id="element-217">
                  <X className="w-5 h-5" data-id="element-218" />
                </button>
              </div>

              {/* Content */}
              <div className="px-6 py-6 overflow-y-auto max-h-[calc(80vh-64px)] text-[var(--text-primary)] text-sm leading-relaxed space-y-4" data-id="element-219">
                {activeModal === 'privacy' ? <PrivacyContent data-id="element-220" /> : <TermsContent data-id="element-221" />}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}
function PrivacyContent() {
  return <>
      <p className="text-[var(--text-muted)] text-xs uppercase tracking-widest mb-4" data-id="element-222">
        Última actualización: Febrero 2026
      </p>

      <h4 className="text-base font-semibold text-[var(--text-heading)] mt-6 mb-2" data-id="element-223">
        1. Información que recopilamos
      </h4>
      <p data-id="element-224">
        Al adquirir "Apaga La Mente", recopilamos únicamente la información
        necesaria para procesar tu compra: dirección de correo electrónico y
        datos de pago. No almacenamos datos de tarjetas de crédito directamente;
        estos son procesados de forma segura por nuestro proveedor de pagos.
      </p>

      <h4 className="text-base font-semibold text-[var(--text-heading)] mt-6 mb-2" data-id="element-225">
        2. Uso de la información
      </h4>
      <p data-id="element-226">Tu información se utiliza exclusivamente para:</p>
      <ul className="list-disc pl-5 space-y-1 text-[var(--text-muted)]" data-id="element-227">
        <li data-id="element-228">Entregar el producto digital (método) a tu correo electrónico</li>
        <li data-id="element-229">Enviar confirmación de compra</li>
        <li data-id="element-230">Brindarte soporte si lo necesitas</li>
      </ul>

      <h4 className="text-base font-semibold text-[var(--text-heading)] mt-6 mb-2" data-id="element-231">
        3. Compartir información
      </h4>
      <p data-id="element-232">
        No vendemos, alquilamos ni compartimos tu información personal con
        terceros, excepto los proveedores de servicios necesarios para procesar
        tu compra y entrega del producto.
      </p>

      <h4 className="text-base font-semibold text-[var(--text-heading)] mt-6 mb-2" data-id="element-233">
        4. Cookies
      </h4>
      <p data-id="element-234">
        Este sitio puede utilizar cookies esenciales para el funcionamiento
        básico de la página. No utilizamos cookies de seguimiento publicitario.
      </p>

      <h4 className="text-base font-semibold text-[var(--text-heading)] mt-6 mb-2" data-id="element-235">
        5. Seguridad
      </h4>
      <p data-id="element-236">
        Implementamos medidas de seguridad razonables para proteger tu
        información personal. Sin embargo, ninguna transmisión por Internet es
        100% segura.
      </p>

      <h4 className="text-base font-semibold text-[var(--text-heading)] mt-6 mb-2" data-id="element-237">
        6. Tus derechos
      </h4>
      <p data-id="element-238">
        Puedes solicitar la eliminación de tus datos personales en cualquier
        momento. Para ejercer este derecho, contáctanos y procesaremos tu
        solicitud en un plazo razonable.
      </p>
    </>;
}
function TermsContent() {
  return <>
      <p className="text-[var(--text-muted)] text-xs uppercase tracking-widest mb-4" data-id="element-239">
        Última actualización: Febrero 2026
      </p>

      <h4 className="text-base font-semibold text-[var(--text-heading)] mt-6 mb-2" data-id="element-240">
        1. Producto
      </h4>
      <p data-id="element-241">
        "Apaga La Mente: Protocolo de 14 Noches" es un producto digital en
        formato de método descargable. Al completar la compra, recibirás acceso
        inmediato al archivo descargable.
      </p>

      <h4 className="text-base font-semibold text-[var(--text-heading)] mt-6 mb-2" data-id="element-242">
        2. Uso personal
      </h4>
      <p data-id="element-243">
        El contenido del método es para uso personal y privado. No está
        permitido redistribuir, copiar, revender o compartir el material sin
        autorización expresa por escrito.
      </p>

      <h4 className="text-base font-semibold text-[var(--text-heading)] mt-6 mb-2" data-id="element-244">
        3. Naturaleza del contenido
      </h4>
      <p data-id="element-245">
        Este producto es de carácter educativo e informativo. No constituye
        consejo médico, psicológico ni terapéutico. Si experimentas problemas
        severos de sueño, ansiedad clínica u otras condiciones de salud,
        consulta a un profesional de la salud.
      </p>

      <h4 className="text-base font-semibold text-[var(--text-heading)] mt-6 mb-2" data-id="element-246">
        4. Resultados
      </h4>
      <p data-id="element-247">
        Los resultados pueden variar de persona a persona. No garantizamos
        resultados específicos, ya que dependen de factores individuales y de la
        aplicación consistente del protocolo.
      </p>

      <h4 className="text-base font-semibold text-[var(--text-heading)] mt-6 mb-2" data-id="element-248">
        5. Política de reembolso
      </h4>
      <p data-id="element-249">
        Debido a la naturaleza digital del producto, todas las ventas son
        finales. Si tienes algún problema con tu compra o acceso al archivo,
        contáctanos y haremos lo posible por ayudarte.
      </p>

      <h4 className="text-base font-semibold text-[var(--text-heading)] mt-6 mb-2" data-id="element-250">
        6. Propiedad intelectual
      </h4>
      <p data-id="element-251">
        Todo el contenido, incluyendo textos, diseño y metodología, está
        protegido por derechos de autor. El uso no autorizado puede resultar en
        acciones legales.
      </p>

      <h4 className="text-base font-semibold text-[var(--text-heading)] mt-6 mb-2" data-id="element-252">
        7. Modificaciones
      </h4>
      <p data-id="element-253">
        Nos reservamos el derecho de modificar estos términos en cualquier
        momento. Los cambios serán efectivos al momento de su publicación en
        esta página.
      </p>
    </>;
}